import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useSupabaseMetrics } from "@/hooks/useSupabaseMetrics";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend
} from 'recharts';
import { format } from 'date-fns';
import { Database, Activity, Server } from 'lucide-react';

export const SupabasePerformance = () => {
  const { data: metrics } = useSupabaseMetrics();
  const lastMetric = metrics?.[0];

  if (!lastMetric) {
    return null;
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Database Performance</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center space-x-4">
              <Database className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">Connection Pool</p>
                <p className="text-2xl font-bold">
                  {lastMetric.connection_pool_stats.active}/{lastMetric.connection_pool_stats.max_connections}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <Activity className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">Query Performance</p>
                <p className="text-2xl font-bold">
                  {lastMetric.query_performance.avg_execution_time.toFixed(2)}ms
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <Server className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">Storage Usage</p>
                <p className="text-2xl font-bold">
                  {(lastMetric.storage_analytics.total_size / 1024 / 1024).toFixed(2)} MB
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Query Performance Trends</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={metrics}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="created_at" 
                  tickFormatter={(value) => format(new Date(value), 'HH:mm')}
                />
                <YAxis />
                <Tooltip 
                  labelFormatter={(value) => format(new Date(value), 'HH:mm:ss')}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="query_performance.avg_execution_time"
                  name="Avg Query Time"
                  stroke="#8884d8"
                />
                <Line
                  type="monotone"
                  dataKey="query_performance.slow_queries"
                  name="Slow Queries"
                  stroke="#82ca9d"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Storage Analytics</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={metrics?.slice(0, 10)}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="created_at"
                  tickFormatter={(value) => format(new Date(value), 'HH:mm')}
                />
                <YAxis />
                <Tooltip 
                  labelFormatter={(value) => format(new Date(value), 'HH:mm:ss')}
                />
                <Legend />
                <Bar
                  dataKey="storage_analytics.files_count"
                  name="Files Count"
                  fill="#8884d8"
                />
                <Bar
                  dataKey="storage_analytics.total_size"
                  name="Total Size (bytes)"
                  fill="#82ca9d"
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};